import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Gift, Wallet } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useTextosDaLoja } from '@/construtor/textos';
import { Caixa } from '@/construtor/caixa';
import { useStore } from '@/contexts/StoreContext';
import { ehLojaDigital } from '@/lib/loja-digital';

export function PaymentSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTextosDaLoja();
  const { store } = useStore();
  // Loja de produtos digitais: a entrega já está em "Minhas compras", não no jogo.
  const lojaDigital = ehLojaDigital(store);

  const paymentId = searchParams.get('payment_id');
  const orderId = searchParams.get('order_id');
  const isFree = searchParams.get('free') === 'true';
  const isCredit = searchParams.get('credit') === 'true';

  useEffect(() => {
    localStorage.removeItem('cart');
  }, []);

  const icon = isFree
    ? <Gift className="w-16 h-16 text-success" />
    : isCredit
      ? <Wallet className="w-16 h-16 text-primary" />
      : <CheckCircle className="w-16 h-16 text-success" />;

  const title = isFree
    ? t('payment.success.free_title')
    : isCredit
      ? t('payment.success.credit_title')
      : t('payment.success.title');

  const description = isCredit
    ? t('payment.success.credit_desc')
    : lojaDigital
      ? t('payment.success.digital_desc')
      : isFree
        ? t('payment.success.free_desc')
        : t('payment.success.desc');

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-background">
      <Caixa id="pagamento.aprovado.cartao" miolo>
      <Card className="max-w-md w-full">
        <CardContent className="pt-6 text-center space-y-4">
          <Caixa id="pagamento.aprovado.icone">
          <div className="flex justify-center">
            {icon}
          </div>
          </Caixa>

          <Caixa id="pagamento.aprovado.titulo">
          <h1 className="text-2xl font-bold text-foreground">
            {title}
          </h1>
          </Caixa>

          <Caixa id="pagamento.aprovado.mensagem">
          <p className="text-muted-foreground">
            {description}
          </p>
          </Caixa>

          {paymentId && (
            <Caixa id="pagamento.aprovado.referencia">
            <div className="bg-muted p-3 rounded-lg text-sm">
              <p className="text-muted-foreground">{t('payment.success.payment_id')}</p>
              <p className="font-mono font-semibold">{paymentId}</p>
            </div>
            </Caixa>
          )}

          {orderId && !paymentId && (
            <Caixa id="pagamento.aprovado.referencia">
            <div className="bg-muted p-3 rounded-lg text-sm">
              <p className="text-muted-foreground">{t('payment.success.order_id')}</p>
              <p className="font-mono font-semibold">{orderId}</p>
            </div>
            </Caixa>
          )}

          <Caixa id="pagamento.aprovado.botoes">
          <div className="space-y-2 pt-4">
            {isCredit ? (
              <>
                <Button
                  onClick={() => navigate('/perfil?tab=credits')}
                  className="w-full"
                >
                  {t('payment.success.view_credits')}
                </Button>
                <Button
                  onClick={() => navigate('/')}
                  variant="outline"
                  className="w-full"
                >
                  {t('payment.success.back_to_store')}
                </Button>
              </>
            ) : (
              <>
                {lojaDigital && (
                  <Button onClick={() => navigate('/perfil?tab=library')} className="w-full" data-testid="ver-minhas-compras">
                    {t('payment.success.view_library')}
                  </Button>
                )}
                <Button
                  onClick={() => navigate('/')}
                  className="w-full"
                  variant={lojaDigital ? 'outline' : 'default'}
                >
                  {t('payment.success.back_to_store')}
                </Button>
                <Button
                  onClick={() => navigate('/perfil?tab=orders')}
                  variant="outline"
                  className="w-full"
                >
                  {t('payment.success.view_orders')}
                </Button>
              </>
            )}
          </div>
          </Caixa>
        </CardContent>
      </Card>
      </Caixa>
    </div>
  );
}
